import React from 'react';
import { Check } from 'lucide-react';

interface DiasVisitaSelectorProps {
  diasSeleccionados: string[];
  onDiasChange: (dias: string[]) => void;
  className?: string;
  disabled?: boolean;
  error?: string;
}

const DIAS_SEMANA = [
  { id: 'lunes', label: 'Lunes', abrev: 'L' },
  { id: 'martes', label: 'Martes', abrev: 'M' },
  { id: 'miércoles', label: 'Miércoles', abrev: 'X' },
  { id: 'jueves', label: 'Jueves', abrev: 'J' },
  { id: 'viernes', label: 'Viernes', abrev: 'V' },
  { id: 'sábado', label: 'Sábado', abrev: 'S' },
  { id: 'domingo', label: 'Domingo', abrev: 'D' }
];

export const DiasVisitaSelector: React.FC<DiasVisitaSelectorProps> = ({
  diasSeleccionados = [],
  onDiasChange,
  className = '',
  disabled = false,
  error
}) => {
  const toggleDia = (diaId: string) => {
    if (disabled) return;
    
    const nuevaSeleccion = diasSeleccionados.includes(diaId)
      ? diasSeleccionados.filter(d => d !== diaId)
      : [...diasSeleccionados, diaId];
    
    onDiasChange(nuevaSeleccion);
  };

  return (
    <div className={className}>
      <div className="grid grid-cols-7 gap-2">
        {DIAS_SEMANA.map((dia) => {
          const isSelected = diasSeleccionados.includes(dia.id);
          
          return (
            <button
              key={dia.id}
              type="button"
              onClick={() => toggleDia(dia.id)}
              disabled={disabled}
              className={`
                relative flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all duration-200
                ${isSelected 
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' 
                  : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'
                }
                ${disabled 
                  ? 'opacity-50 cursor-not-allowed' 
                  : 'hover:shadow-sm cursor-pointer'
                }
              `}
              title={dia.label}
            >
              {/* Indicador de selección */}
              {isSelected && (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
              
              {/* Abreviación del día */}
              <span className="text-lg font-semibold">
                {dia.abrev}
              </span>
              
              {/* Nombre completo en pantallas grandes */}
              <span className="text-xs mt-1 hidden sm:block">
                {dia.label}
              </span>
            </button>
          );
        })}
      </div>
      
      {/* Mensaje de error */}
      {error && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
      
      {/* Días seleccionados (texto) */}
      {diasSeleccionados.length > 0 && (
        <div className="mt-3 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium">Días seleccionados:</span>{' '}
            {diasSeleccionados
              .map(id => DIAS_SEMANA.find(d => d.id === id)?.label)
              .filter(Boolean)
              .join(', ')
            }
          </p>
        </div>
      )}
    </div>
  );
};
